import { Component, ErrorInfo } from 'react'
import * as cn from 'classnames'
import * as styles from 'components/features/pfm/App/App.css'
import SubtreeTracker from 'components/utils/SubtreeTracker'
import OuterEventTracker from 'components/utils/OuterEventTracker'
import { NavigateToHandler } from 'handlers/NavigateToHandler/NavigateToHandler'
import { Communication } from 'tangle-shared'
import ErrorPageContainer from 'components/features/pfm/ErrorPage/ErrorPageContainer'
import { isInternalPageUrlWithSidebar, isShrunkSidebarUrl } from 'helpers/pageHelpers'
import { remote } from 'helpers/mobileAppHelpers'
import AccomplishmentModalContainer from 'components/features/pfm/AccomplishmentModal/AccomplishmentModalContainer'
import { IS_IOS } from 'helpers/userAgentHelpers'
import { perfMeasure, PERF_MARKS } from 'helpers/analyticsHelpers'
import { URL_PAGE_MAP } from 'constants/pages'
import UpdateNowPopup from 'components/features/pfm/UpdateNowPopup/UpdateNowPopup'
import { pageChangedActionCreator } from 'actions/globalActions'
import { RouteComponentProps } from 'react-router'
import { makeUrlRelative } from 'helpers/utilsHelpers'
import { TooltipZoneNodeContext } from 'components/features/pfm/Tooltip/Tooltip'
import { NotificationTrayContextProvider } from 'components/features/pfm/NotificationTray/NotificationTrayContextProvider'
import { NotificationTrayWrapper } from 'components/features/pfm/NotificationTray/NotificationTrayWrapper'
import { FloatingPanesHost } from 'components/features/pfm/FloatingPaneHost/FloatingPaneHost'
import { PageMeta } from 'components/features/pfm/PageMeta/PageMeta'
import { PreventScrollContextProvider } from 'components/features/pfm/PreventScroll/PreventScrollContextProvider'
import { ThemeProvider } from 'components/features/theme/ThemeProvider/ThemeProvider'
import * as Tooltip from '@radix-ui/react-tooltip'

export type AppStateProps = {
  isAuthenticated: boolean
  hasError: boolean
  isDataUpdated: boolean
}

export type AppDispatchProps = {
  sendPageChanged: typeof pageChangedActionCreator
  setApplicationError: (error: Error) => void
  setupFlushDate: () => void
  fetchCTA: () => void
}

export type AppOwnProps = RouteComponentProps<{}>

export type AppProps =
  & AppStateProps
  & AppDispatchProps
  & AppOwnProps

export type AppState = {
  prevPageUrl: string | null
}

class App extends Component<AppProps, AppState> {
  state: AppState = {
    prevPageUrl: null,
  }

  private keyboardHidingTimeout = 0

  render () {
    const {
      children,
      isAuthenticated,
      hasError,
      isDataUpdated,
    } = this.props

    const { prevPageUrl } = this.state
    const pageUrl = this.getRelativeUrl()
    const slideable = (!prevPageUrl || isInternalPageUrlWithSidebar(prevPageUrl, isAuthenticated))
      && isInternalPageUrlWithSidebar(pageUrl, isAuthenticated)
    const slidLeft = isShrunkSidebarUrl(pageUrl, isAuthenticated)

    if (hasError) {
      return (
        <ErrorPageContainer />
      )
    }

    const className = cn(styles.app, {
      [styles.slidLeft]: slidLeft,
      [styles.slideable]: slideable,
    })

    // TODO remove crutch after full notifications implementation, need to refactor slidLeft animation
    return (
      <Tooltip.Provider>
        <ThemeProvider>
          <PageMeta
            title='Financial Manager with Peer Comparisons and Advice | Status Money'
            description='Compare finances with peers, get advice, and create a financial plan – for free.'
          />
          <NavigateToHandler />
          { isDataUpdated && <UpdateNowPopup /> }
          <TooltipZoneNodeContext.Provider value={ document.body }>
            <NotificationTrayContextProvider url={ pageUrl }>
              <OuterEventTracker>
                <SubtreeTracker>
                  <PreventScrollContextProvider>
                    <NotificationTrayWrapper
                      theme='settings'
                      renderLevel='app'
                    />
                    <AccomplishmentModalContainer />
                    <FloatingPanesHost />
                    <div className={ className }>
                      { children }
                    </div>
                  </PreventScrollContextProvider>
                </SubtreeTracker>
              </OuterEventTracker>
            </NotificationTrayContextProvider>
          </TooltipZoneNodeContext.Provider>
        </ThemeProvider>
      </Tooltip.Provider>
    )
  }

  componentDidMount () {
    const { sendPageChanged, setupFlushDate, fetchCTA } = this.props
    const pageUrl = this.getRelativeUrl()
    setupFlushDate()
    sendPageChanged({
      next: pageUrl,
      prev: null,
      action: 'initial',
    })

    remote.on<Communication.KeyboardAppearanceEvent>('keyboard-appearance', this.handleKeyboardAppearance)

    if (IS_IOS) {
      document.addEventListener('focus', this.handleFocus, true)
      document.addEventListener('blur', this.handleBlur, true)
    }

    const pageName = URL_PAGE_MAP[pageUrl] || 'unknown'
    perfMeasure(PERF_MARKS.pageFMP, pageName)
    fetchCTA()
  }

  componentDidUpdate (prevProps: AppProps) {
    const { history, sendPageChanged } = this.props
    const prevRelativeUrl = makeUrlRelative(prevProps.location)
    const nextRelativeUrl = this.getRelativeUrl()
    if (nextRelativeUrl !== prevRelativeUrl) {
      sendPageChanged({
        next: nextRelativeUrl,
        prev: prevRelativeUrl,
        action: history.action === 'PUSH' ? 'push' : 'pop',
      })
    }
  }

  // we may use error info for something but now we have error stack in sentry
  componentDidCatch (error: Error, _info: ErrorInfo) {
    const { setApplicationError } = this.props
    setApplicationError(error)
  }

  componentWillUnmount () {
    clearTimeout(this.keyboardHidingTimeout)
    if (IS_IOS) {
      document.removeEventListener('focus', this.handleFocus, true)
      document.removeEventListener('blur', this.handleBlur, true)
    }
  }

  private getRelativeUrl () {
    const { location } = this.props
    return makeUrlRelative(location)
  }

  private handleFocus = () => {
    this.handleKeyboardAppearance({ shown: true })
  }

  private handleBlur = () => {
    this.handleKeyboardAppearance({ shown: false })
  }

  private handleKeyboardAppearance = (payload: Communication.KeyboardAppearanceEventData) => {
    if (payload.shown) {
      clearTimeout(this.keyboardHidingTimeout)
      return
    }

    // Timeout is necessary to prevent page jarring when a keyboard gets closed and opened just after
    // (a cursor gets to another field)
    this.keyboardHidingTimeout = window.setTimeout(() => {
      window.scrollBy(1, 0)
    }, 50)
  }
}

export default App
